import React, { useEffect, useMemo, useState } from 'react'
import PlayerPayments from './PlayerPayments'
import { apiGet } from '../utils/api'

// Функция для форматирования чисел с разделителями тысяч в русской локали (округление до сотых)
const formatNumber = (num: number): string => {
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

type Row = { id: string; name: string; type: string; opening: number; incoming: number; outgoing: number; closing: number }
type Detail = { id?: string; date: string; type: string; amount: number; description?: string; playerId?: string; tournamentId?: string }

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'CASH': return 'Касса (наличные)'
    case 'BANK': return 'Банковский счет'
    case 'CARD': return 'Карта'
    default: return type
  }
}

const ReportsCashMovements: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [from, setFrom] = useState(() => {
    const saved = localStorage.getItem('cashMovementsFrom')
    return saved || ''
  })
  const [to, setTo] = useState(() => {
    const saved = localStorage.getItem('cashMovementsTo')
    return saved || ''
  })
  const [detailsFor, setDetailsFor] = useState<string | null>(null)
  const [details, setDetails] = useState<Detail[]>([])
  const [dLoading, setDLoading] = useState(false)
  const [editPayment, setEditPayment] = useState<any | null>(null)

  const total = useMemo(() => rows.reduce((a, r) => ({
    opening: a.opening + r.opening,
    incoming: a.incoming + r.incoming,
    outgoing: a.outgoing + r.outgoing,
    closing: a.closing + r.closing,
  }), { opening: 0, incoming: 0, outgoing: 0, closing: 0 }), [rows])

  const fetchData = async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      const data = await apiGet<any[]>('/reports/cash-movements', params)
      setRows(Array.isArray(data) ? data : [])
    } catch (e: any) {
      console.error('Error fetching cash movements:', e)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  const handleFromChange = (value: string) => {
    setFrom(value)
    localStorage.setItem('cashMovementsFrom', value)
  }

  const handleToChange = (value: string) => {
    setTo(value)
    localStorage.setItem('cashMovementsTo', value)
  }

  const resetPeriod = () => {
    setFrom('')
    setTo('')
    localStorage.removeItem('cashMovementsFrom')
    localStorage.removeItem('cashMovementsTo')
  }

  const openDetails = async (accountId: string) => {
    setDLoading(true)
    setDetailsFor(accountId)
    try {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      const data = await apiGet<any[]>(`/reports/cash-movements/${accountId}/details`, params)
      setDetails(Array.isArray(data) ? data : [])
    } catch (e: any) {
      console.error('Error fetching cash movement details:', e)
      setDetails([])
    } finally {
      setDLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [from, to])

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900">Движение денег</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">С даты</label>
          <input type="date" className="border rounded px-3 py-2" value={from} onChange={e=>handleFromChange(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">По дату</label>
          <input type="date" className="border rounded px-3 py-2" value={to} onChange={e=>handleToChange(e.target.value)} />
        </div>
        <button className="px-4 py-2 border rounded bg-white hover:bg-gray-50" onClick={fetchData}>Обновить</button>
        <button className="px-4 py-2 border rounded bg-gray-100 hover:bg-gray-200 text-gray-700" onClick={resetPeriod}>Сбросить</button>
      </div>

      <div className="overflow-auto">
        <table className="min-w-full bg-white border rounded">
          <thead>
            <tr className="bg-gray-50 text-left text-sm text-gray-600">
              <th className="px-3 py-2 border-b text-left">Счёт</th>
              <th className="px-3 py-2 border-b text-right">Нач.ост.</th>
              <th className="px-3 py-2 border-b text-right">Поступления</th>
              <th className="px-3 py-2 border-b text-right">Списания</th>
              <th className="px-3 py-2 border-b text-right">Кон.ост.</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading && (<tr><td className="px-3 py-3 text-gray-500" colSpan={5}>Загрузка...</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td className="px-3 py-3 text-gray-500" colSpan={5}>Нет данных</td></tr>)}
            {!loading && rows.map(r => (
              <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={()=>openDetails(r.id)}>
                <td className="px-3 py-2 border-b text-left">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-gray-500">{getTypeLabel(r.type)}</div>
                </td>
                <td className="px-3 py-2 border-b text-right">{formatNumber(r.opening)}</td>
                <td className="px-3 py-2 border-b text-right">{formatNumber(r.incoming)}</td>
                <td className="px-3 py-2 border-b text-right">{formatNumber(r.outgoing)}</td>
                <td className="px-3 py-2 border-b text-right font-medium">{formatNumber(r.closing)}</td>
              </tr>
            ))}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 text-base font-bold">
                <td className="px-3 py-2 border-t font-bold text-left">Итого</td>
                <td className="px-3 py-2 border-t text-right">{formatNumber(total.opening)}</td>
                <td className="px-3 py-2 border-t text-right">{formatNumber(total.incoming)}</td>
                <td className="px-3 py-2 border-t text-right">{formatNumber(total.outgoing)}</td>
                <td className="px-3 py-2 border-t text-right">{formatNumber(total.closing)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {detailsFor && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-3xl rounded shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-gray-900">Детализация: {rows.find(r => r.id === detailsFor)?.name || 'Счет'}</div>
              <button className="px-3 py-1 border rounded hover:bg-gray-50" onClick={()=>{ setDetailsFor(null); setDetails([]) }}>Закрыть</button>
            </div>
            <div className="overflow-auto max-h-[70vh]">
              <table className="min-w-full bg-white border rounded">
                <thead>
                  <tr className="bg-gray-50 text-left text-sm text-gray-600">
                    <th className="px-3 py-2 border-b">Дата</th>
                    <th className="px-3 py-2 border-b">Описание</th>
                    <th className="px-3 py-2 border-b text-right">Поступления</th>
                    <th className="px-3 py-2 border-b text-right">Списания</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {dLoading && (<tr><td className="px-3 py-3 text-gray-500" colSpan={4}>Загрузка...</td></tr>)}
                  {!dLoading && details.length === 0 && (<tr><td className="px-3 py-3 text-gray-500" colSpan={4}>Нет данных</td></tr>)}
                  {!dLoading && details.length > 0 && (
                    <tr className="bg-gray-50 font-medium">
                      <td className="px-3 py-2 border-b">ИТОГО</td>
                      <td className="px-3 py-2 border-b">-</td>
                      <td className="px-3 py-2 border-b text-right">
                        {formatNumber(details.reduce((sum, d) => {
                          const amount = Number(d.amount || 0)
                          return sum + (amount > 0 ? Math.abs(amount) : 0)
                        }, 0))}
                      </td>
                      <td className="px-3 py-2 border-b text-right">
                        {formatNumber(details.reduce((sum, d) => {
                          const amount = Number(d.amount || 0)
                          return sum + (amount < 0 ? Math.abs(amount) : 0)
                        }, 0))}
                      </td>
                    </tr>
                  )}
                  {!dLoading && details.map((d, idx) => {
                    const amount = Number(d.amount || 0)
                    const displayAmount = Math.abs(amount)
                    
                    // Теперь только PAYMENT операции: положительные = поступления, отрицательные = списания
                    const isIncoming = amount > 0
                    const isOutgoing = amount < 0
                    
                    return (
                      <tr key={idx}>
                        <td className="px-3 py-2 border-b whitespace-nowrap">{d.date?.slice(0,10)}</td>
                        <td className="px-3 py-2 border-b">{d.description || '-'}</td>
                        <td className="px-3 py-2 border-b text-right">
                          {isIncoming ? (
                            d.id ? (
                              d.description?.includes('Выручка от продажи ТМЦ') ? (
                                <button
                                  className="text-blue-600 hover:underline cursor-pointer"
                                  onClick={() => {
                                    // Извлекаем номер документа из описания
                                    const match = d.description?.match(/INV-SAL-\d+-\d+/)
                                    if (match) {
                                      const saleNumber = match[0]
                                      // Переходим к странице продаж ТМЦ
                                      window.open(`/inventory-sales?search=${saleNumber}`, '_blank')
                                    }
                                  }}
                                >{formatNumber(displayAmount)}</button>
                              ) : d.playerId ? (
                                <button
                                  className="text-blue-600 hover:underline cursor-pointer"
                                  onClick={async ()=>{
                                    try {
                                      const data = await apiGet<any>(`/player-payments/${d.id}`)
                                      setEditPayment(data)
                                    } catch (e) { 
                                      console.error('Error loading player payment:', e) 
                                    }
                                  }}
                                >{formatNumber(displayAmount)}</button>
                              ) : (
                                formatNumber(displayAmount)
                              )
                            ) : (
                              formatNumber(displayAmount)
                            )
                          ) : '-'}
                        </td>
                        <td className="px-3 py-2 border-b text-right">
                          {isOutgoing ? formatNumber(displayAmount) : '-'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {editPayment && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-2xl rounded shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Оплата игрока</div>
              <button className="px-3 py-1 border rounded" onClick={()=>setEditPayment(null)}>Закрыть</button>
            </div>
            <div className="space-y-2 text-sm">
              <div><b>Игрок:</b> {editPayment?.player?.name || editPayment?.playerId}</div>
              <div><b>Турнир:</b> {editPayment?.tournament?.name || editPayment?.tournamentId || '-'}</div>
              <div><b>Дата:</b> {String(editPayment?.createdAt).slice(0,10)}</div>
              <div><b>Сумма:</b> {formatNumber(Number(editPayment?.amount||0))}</div>
              <div className="text-gray-600">Для изменения/удаления откройте журнал «Оплаты игроков».</div>
              <div>
                <a className="text-blue-600 hover:underline" href="/documents/player-payments" target="_blank" rel="noreferrer">Открыть журнал оплат</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportsCashMovements


